import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Typography from "../Typography";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { type FormEventHandler, useCallback, useMemo, useState } from "react";
import { useUserId } from "../../hooks/useUserId";
import {
  PresenceTableName,
  useDynamoDeleteItem,
  useDynamoMutateItem,
} from "../../services/dynamo";
import { DayStringFormat, type DateString, type PresenceSingle, type PresenceTypeSingle } from "../../types/presence";
import { toast } from "sonner";
import { formatPermitHours } from "@/lib/utils";

type WorkPermitFormProps = {
  date: Date;
  data?: PresenceSingle;
  reason?: PresenceTypeSingle;
  triggerAnnulla: () => void;
};

export default function WorkPermitForm({
  data,
  date,
  reason: reasonProp = "permesso",
  triggerAnnulla,
}: WorkPermitFormProps) {
  const [hoursNum, setHoursNum] = useState<number>(Math.floor(data?.hours ?? 1));
  const [halfHour, setHalfHour] = useState<boolean>((data?.hours ?? 0) % 1 !== 0);
  const hours = hoursNum + (halfHour ? 0.5 : 0);
  const userId = useUserId();

  const partialRecord = useMemo<
    Pick<PresenceSingle, "pk" | "sk" | "date" | "reason">
  >(
    () =>
      data ?? {
        pk: `${userId!}`,
        sk: `presenceDay#${format(date, DayStringFormat) as DateString}`,
        reason: reasonProp,
        date: format(date, DayStringFormat),
      },
    [data, userId, date, reasonProp],
  );

  const { mutate, isPending } = useDynamoMutateItem<PresenceSingle>(
    PresenceTableName,
    {},
  );
  const { mutate: deleteRecord, isPending: isPendingDelete } =
    useDynamoDeleteItem(PresenceTableName, {});

  const isSomethingPending = isPending || isPendingDelete;

  const onSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    mutate(
      { ...partialRecord, hours },
      {
        onSuccess: (saved) => {
          toast.success(`${reasonProp === "104" ? "Permesso 104" : "Permesso"} registrato: ${formatPermitHours(saved.hours ?? 0)}`);
          triggerAnnulla();
        },
        onError: () => toast.error("Errore durante il salvataggio"),
      },
    );
  };

  const handleHoursChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value);
      const clamped = isNaN(val) ? 0 : Math.max(0, Math.min(8, val));
      setHoursNum(clamped);
      if (clamped >= 8) setHalfHour(false);
    },
    [],
  );

  const handleDelete = useCallback(() => {
    deleteRecord(
      {
        key: { pk: partialRecord.pk, sk: partialRecord.sk },
        lastUpdate: data!.lastUpdate!,
      },
      {
        onSuccess: () => {
          toast.success("Registrazione eliminata");
          triggerAnnulla();
        },
        onError: () => toast.error("Errore durante l'eliminazione"),
      },
    );
  }, [deleteRecord, partialRecord, data, triggerAnnulla]);

  return (
    <div className="WorkPermitForm">
      <form id="work-permit-form" onSubmit={onSubmit}>
        <Typography variant="body2">
          {reasonProp === "104" ? "Permesso 104" : "Permesso"} per il giorno {format(date, "PPP", { locale: it })}
        </Typography>
        <div className="WorkPermitForm__time-picker">
          <div className="WorkPermitForm__time-field">
            <Typography variant="body2">Ore</Typography>
            <Input
              type="number"
              placeholder="Ore"
              value={hoursNum}
              min={0}
              max={8}
              step={1}
              onChange={handleHoursChange}
            />
          </div>
          <div className="WorkPermitForm__halfhour">
            <label className="WorkPermitForm__halfhour-label">
              <input
                type="checkbox"
                checked={halfHour}
                disabled={hoursNum >= 8}
                onChange={(e) => setHalfHour(e.target.checked)}
              />
              <Typography variant="body2">+ 30 minuti</Typography>
            </label>
          </div>
        </div>
        {hours > 0 && (
          <Typography variant="body2">
            Totale: {formatPermitHours(hours)}
          </Typography>
        )}
      </form>

      <div className="WorkPermitForm__buttons">
        {data && !isSomethingPending && (
          <Button variant="ghost" onClick={handleDelete}>
            Elimina
          </Button>
        )}
        {!isSomethingPending && (
          <Button type="submit" form="work-permit-form" disabled={hours === 0}>
            {data ? "Aggiorna" : "Salva"}
          </Button>
        )}
      </div>
    </div>
  );
}
